import {
  hostedAssistantsCreate,
  hostedAssistantsList,
  hostedAssistantsUpdate,
  hostedChatMessagesAppendToMessage,
  hostedChatMessagesDeleteMessage,
  hostedChatMessagesGetForSession,
  hostedChatMessagesUpsertMessage,
  hostedChatSessionMetadataDeleteSession,
  hostedChatSessionMetadataGetForWorkspace,
  hostedChatSessionMetadataSetDisplayName,
  hostedChatSessionMetadataUpsertMetadata,
  splitThinkingFromChatContent,
} from '@owlbert/core'
import {
  ActionIcon,
  Avatar,
  Box,
  Button,
  Center,
  CopyButton,
  Divider,
  Flex,
  Group,
  Loader,
  Menu,
  Modal,
  Paper,
  ScrollArea,
  Select,
  Stack,
  Text,
  TextInput,
  Textarea,
  Title,
  Tooltip,
} from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { notifications } from '@mantine/notifications'
import { IconArrowLeft, IconBooks, IconCamera, IconCheck, IconChevronDown, IconCopy, IconMessageCircle, IconPaperclip, IconPencil, IconRefresh, IconRobot, IconTrash, IconUserPlus } from '@tabler/icons-react'
import React from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  createAssistant,
  fetchAssistants,
  updateAssistant,
  type FetchAssistantsResponse,
  type HostedAssistantRow,
} from '../api'
import type { AssistantProposalFields } from '../assistant/assistantProposalFromDescription'
import { useOwlbertWebAuth } from '../auth/OwlbertWebAuthContext'
import { AssistantAutoCreatePanel } from '../components/AssistantAutoCreatePanel'
import { AssistantCatalogPickerModal } from '../components/AssistantCatalogPickerModal'
import { AssistantModelField } from '../components/AssistantModelField'
import { isTruncatedAssistantFinishReason, WEB_CHAT_CONTINUE_PROMPT } from '../chat/chatTruncation'

import { parseAssistantsToolsCsv } from '../components/HostedAssistantManager'
import { ChatMessageContent } from '../components/ChatMessageContent'
import {
  runWebChatAutoWithKbTool,
  formatOwlbertAttachmentImageMarkdown,
  type WebChatAutoMessage,
} from '../chat/runWebChatAutoWithKbTool'
import { ChatAttachmentChips, type ChatAttachmentDraft } from '../components/ChatAttachmentChips'
import {
  CHAT_ATTACHMENT_MAX_BYTES,
  CHAT_ATTACHMENT_MAX_COUNT,
  formatDocumentAttachmentsForModel,
  isImageAttachmentFile,
  prepareChatAttachment,
  type PreparedChatDocumentAttachment,
  type PreparedChatImageAttachment,
} from '../chat/chatAttachments'
import { indexBrowserHostedUserDocumentToKb } from '../knowledgebase/indexBrowserHostedUserDocument'
import { hostedUserDocumentsAdd, hostedSessionAttachmentsAdd } from '@owlbert/core'
import { maybeAutoTitleWebChatSession } from '../chat/autoTitleWebChatSession'
import { webLog } from '../logging/webLog'
import { WEB_DEFAULT_ASSISTANT_INSTRUCTIONS, withDefaultOwlbertAssistant } from '../webAssistantDefaults'

const ChatComposer = React.memo(function ChatComposer({
  disabled,
  sending,
  busy,
  onSend,
  onStop,
  isMobileChatStack,
  assistantName,
}: {
  disabled: boolean
  sending: boolean
  busy: boolean
  onSend: (text: string, attachments: ChatAttachmentDraft[]) => void
  onStop: () => void
  isMobileChatStack: boolean
  assistantName?: string
}) {
  const [draft, setDraft] = React.useState('')
  const [attachments, setAttachments] = React.useState<ChatAttachmentDraft[]>([])
  const [dragActive, setDragActive] = React.useState(false)
  const fileInputRef = React.useRef<HTMLInputElement | null>(null)
  const cameraInputRef = React.useRef<HTMLInputElement | null>(null)

  const addFiles = React.useCallback((files: readonly File[]) => {
    if (!files.length) return
    setAttachments((prev) => {
      const room = CHAT_ATTACHMENT_MAX_COUNT - prev.length
      if (room <= 0) {
        notifications.show({ color: 'yellow', message: `You can attach up to ${CHAT_ATTACHMENT_MAX_COUNT} files per message.` })
        return prev
      }
      const accepted: ChatAttachmentDraft[] = []
      for (const file of files.slice(0, room)) {
        if (file.size > CHAT_ATTACHMENT_MAX_BYTES) {
          notifications.show({ color: 'yellow', message: `${file.name} is too large (max ${CHAT_ATTACHMENT_MAX_BYTES / (1024 * 1024)} MB).` })
          continue
        }
        const isImage = isImageAttachmentFile(file)
        accepted.push({
          id: crypto.randomUUID(),
          file,
          name: file.name || (isImage ? 'photo' : 'attachment'),
          isImage,
          previewUrl: isImage ? URL.createObjectURL(file) : undefined,
        })
      }
      if (files.length > room) {
        notifications.show({ color: 'yellow', message: `Only ${room} more file(s) could be attached.` })
      }
      return accepted.length ? [...prev, ...accepted] : prev
    })
  }, [])

  const removeAttachment = React.useCallback((id: string) => {
    setAttachments((prev) => {
      const target = prev.find((a) => a.id === id)
      if (target?.previewUrl) URL.revokeObjectURL(target.previewUrl)
      return prev.filter((a) => a.id !== id)
    })
  }, [])

  const handleSend = () => {
    const text = draft.trim()
    if ((!text && !attachments.length) || disabled || sending) return
    setDraft('')
    setAttachments([])
    onSend(text, attachments)
  }
  return (
    <Box
      className={`owlbert-web-chat-composer${isMobileChatStack ? ' owlbert-web-chat-composer--mobile' : ''}`}
      onDragOver={(e) => {
        if (disabled) return
        e.preventDefault()
        setDragActive(true)
      }}
      onDragLeave={() => setDragActive(false)}
      onDrop={(e) => {
        if (disabled) return
        e.preventDefault()
        setDragActive(false)
        addFiles(Array.from(e.dataTransfer?.files ?? []))
      }}
      style={dragActive ? { outline: '2px dashed var(--mantine-color-blue-5)', outlineOffset: 4, borderRadius: 8 } : undefined}
    >
      <ChatAttachmentChips attachments={attachments} onRemove={removeAttachment} disabled={sending} />
      <input
        ref={fileInputRef}
        type="file"
        multiple
        hidden
        onChange={(e) => {
          addFiles(Array.from(e.currentTarget.files ?? []))
          e.currentTarget.value = ''
        }}
      />
      <input
        ref={cameraInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        hidden
        onChange={(e) => {
          addFiles(Array.from(e.currentTarget.files ?? []))
          e.currentTarget.value = ''
        }}
      />
      <Group align="flex-end" wrap="nowrap" gap="sm">
        <ActionIcon
          variant="subtle"
          color="gray"
          size="lg"
          radius="md"
          aria-label="Attach files"
          title="Attach photos or documents"
          disabled={disabled}
          onClick={() => fileInputRef.current?.click()}
        >
          <IconPaperclip size={18} />
        </ActionIcon>
        {isMobileChatStack ? (
          <ActionIcon
            variant="subtle"
            color="gray"
            size="lg"
            radius="md"
            aria-label="Take a photo"
            title="Take a photo"
            disabled={disabled}
            onClick={() => cameraInputRef.current?.click()}
          >
            <IconCamera size={18} />
          </ActionIcon>
        ) : null}
        <Textarea
          aria-label={`Message ${assistantName || 'assistant'}`}
          style={{ flex: 1, minWidth: 0 }}
          placeholder={`Message ${assistantName || 'assistant'}…`}
          value={draft}
          onChange={(e) => setDraft(e.currentTarget.value)}
          onKeyDown={(e) => {
            if (e.key !== 'Enter' || e.shiftKey) return
            if (e.nativeEvent.isComposing) return
            e.preventDefault()
            handleSend()
          }}
          onPaste={(e) => {
            const pasted = Array.from(e.clipboardData?.files ?? [])
            if (!pasted.length) return
            e.preventDefault()
            addFiles(pasted)
          }}
          radius="md"
          size="md"
          disabled={disabled}
          autosize
          minRows={1}
          maxRows={12}
          resize="none"
        />
        {busy ? (
          <Button radius="md" color="red" variant="light" leftSection={<Loader size={14} color="red" />} onClick={onStop}>
            Stop
          </Button>
        ) : (
          <Button radius="md" loading={sending} disabled={disabled} onClick={handleSend}>
            Send
          </Button>
        )}
      </Group>
    </Box>
  )
})

type ChatRow = {
  id: string
  role: string
  content: string
  timestamp?: string
  finishReason?: string | null
  /** Name of the assistant that produced this assistant-role message (null for user/system). */
  assistantName?: string | null
}

/** Compact, locale-aware message time. Same-day shows time only; older adds the date. */
function formatMessageTime(ts?: string): string {
  if (!ts) return ''
  const d = new Date(ts)
  if (Number.isNaN(d.getTime())) return ''
  const now = new Date()
  const sameDay = d.toDateString() === now.toDateString()
  return sameDay
    ? d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
}

type SessionRow = {
  sessionId: string
  displayName: string | null
  lastAssistantName: string | null
  updatedAt?: number
}

/** Generic conversation starters shown in the empty state (clicking sends the prompt). */
const STARTER_PROMPTS = [
  'What can you help me with?',
  'Summarize a document for me',
  'Help me draft an email',
  'Brainstorm some ideas',
]

const SESSION_ID_ROUTE_RE = /^[0-9a-f-]{36}$/i
/** Match `WebBasicShell` / `WebMobileTabBar` — below this width chat uses list → detail + URL `/assistants/:sessionId`. */
const MOBILE_CHAT_STACK_QUERY = '(max-width: 47.99em)'

/** Chat page: assistants, sessions, and messages. */
export const AssistantsPage: React.FC = () => {
  const navigate = useNavigate()
  const { sessionId: sessionIdFromUrl } = useParams<{ sessionId?: string }>()
  const isMobileChatStack = useMediaQuery(MOBILE_CHAT_STACK_QUERY, false, { getInitialValueInEffect: true })
  const auth = useOwlbertWebAuth()
  const browserReady = auth.configured && auth.sessionReady && Boolean(auth.workspaceId) && Boolean(auth.bundle?.client)
  const catalogReady = auth.sessionReady && Boolean(auth.bundle?.client)

  const [assistants, setAssistants] = React.useState<HostedAssistantRow[]>([])
  const [assistantsMeta, setAssistantsMeta] = React.useState<Pick<FetchAssistantsResponse, 'storage' | 'message' | 'error'>>({
    storage: 'none',
  })

  const [selectedAssistantId, setSelectedAssistantId] = React.useState<string | null>(null)
  const [sessions, setSessions] = React.useState<SessionRow[]>([])
  /** True once the workspace session list has been fetched at least once (empty list is a real answer). */
  const [sessionsLoaded, setSessionsLoaded] = React.useState(false)
  const [selectedSessionId, setSelectedSessionId] = React.useState<string | null>(null)
  const [messages, setMessages] = React.useState<ChatRow[]>([])
  const [sending, setSending] = React.useState(false)
  const [continuing, setContinuing] = React.useState(false)
  const [renameTarget, setRenameTarget] = React.useState<SessionRow | null>(null)
  const [renameValue, setRenameValue] = React.useState('')
  const [deleteTarget, setDeleteTarget] = React.useState<SessionRow | null>(null)
  /** Cooperative cancellation flag for the in-flight generation (Stop button). */
  const cancelRef = React.useRef(false)
  const onStopGeneration = React.useCallback(() => {
    cancelRef.current = true
  }, [])

  const [assistModalOpen, setAssistModalOpen] = React.useState(false)
  const [assistModalMode, setAssistModalMode] = React.useState<'create' | 'edit'>('create')
  const [assistEditingId, setAssistEditingId] = React.useState<string | null>(null)
  const [assistName, setAssistName] = React.useState('')
  const [assistDescription, setAssistDescription] = React.useState('')
  const [assistInstructions, setAssistInstructions] = React.useState('')
  const [assistGreeting, setAssistGreeting] = React.useState('')
  const [assistLlm, setAssistLlm] = React.useState('')
  const [assistTools, setAssistTools] = React.useState('')
  const [assistSaving, setAssistSaving] = React.useState(false)
  const [assistWizardKey, setAssistWizardKey] = React.useState(0)
  const [assistPendingProposal, setAssistPendingProposal] = React.useState<AssistantProposalFields | null>(null)
  const [assistCreateManual, setAssistCreateManual] = React.useState(false)
  const [catalogPickerOpen, setCatalogPickerOpen] = React.useState(false)

  const hostedListOk = assistantsMeta.storage === 'hosted' && !assistantsMeta.error
  // catalogReady: user is signed in with a working client — allow management even if workspace resolution failed
  const canManageAssistants = (browserReady && Boolean(auth.workspaceId)) || hostedListOk || catalogReady

  /** No chats yet and nothing to chat with — prompt to create an assistant first (hides useless New chat). */
  const showAssistantFirstRun =
    browserReady && canManageAssistants && assistants.length === 0 && sessions.length === 0

  /** Keep selection aligned with `/assistants` vs `/assistants/:sessionId` (Chats tab, back, deep links). */
  React.useEffect(() => {
    if (sessionIdFromUrl && SESSION_ID_ROUTE_RE.test(sessionIdFromUrl)) {
      setSelectedSessionId(sessionIdFromUrl)
      return
    }
    if (sessionIdFromUrl && !SESSION_ID_ROUTE_RE.test(sessionIdFromUrl)) {
      navigate('/assistants', { replace: true })
      return
    }
    if (!sessionIdFromUrl) {
      setSelectedSessionId(null)
    }
  }, [sessionIdFromUrl, navigate])

  const reloadAssistantsBff = React.useCallback(async () => {
    try {
      const r = await fetchAssistants()
      setAssistants(withDefaultOwlbertAssistant(r.assistants as HostedAssistantRow[]))
      setAssistantsMeta({ storage: r.storage, message: r.message, error: r.error })
    } catch {
      setAssistants(withDefaultOwlbertAssistant([]))
      setAssistantsMeta({ storage: 'none', message: 'Could not reach /api/assistants.' })
    }
  }, [])

  /** BFF fallback (static token) when browser auth is not active. */
  React.useEffect(() => {
    if (browserReady) return
    void reloadAssistantsBff()
  }, [browserReady, reloadAssistantsBff])

  const loadAssistantsBrowser = React.useCallback(async () => {
    if (!browserReady || !auth.bundle || !auth.workspaceId) return
    const rows = await hostedAssistantsList(auth.bundle.client.owlbertHosted.assistants, { workspaceId: auth.workspaceId })
    const withDefault = withDefaultOwlbertAssistant(rows as unknown as HostedAssistantRow[])
    setAssistants(withDefault)
    setAssistantsMeta({ storage: 'hosted' })
    setSelectedAssistantId((prev) => prev ?? (withDefault[0]?.id ?? null))
  }, [auth.bundle, auth.workspaceId, browserReady])

  const loadChatAttachment = React.useCallback(
    async (attachmentId: string) => {
      if (!browserReady || !auth.bundle || !auth.workspaceId || !selectedSessionId) return null
      return auth.bundle.client.owlbertHosted.sessionAttachments.getContent.query({
        workspaceId: auth.workspaceId,
        chatSessionId: selectedSessionId,
        id: attachmentId,
      })
    },
    [auth.bundle, auth.workspaceId, browserReady, selectedSessionId]
  )

  const openCreateAssistantModal = () => {
    setAssistModalMode('create')
    setAssistEditingId(null)
    setAssistName('')
    setAssistDescription('')
    setAssistInstructions(WEB_DEFAULT_ASSISTANT_INSTRUCTIONS)
    setAssistGreeting('Hello! How can I help?')
    setAssistLlm('')
    setAssistTools('')
    setAssistPendingProposal(null)
    setAssistCreateManual(false)
    setCatalogPickerOpen(false)
    setAssistWizardKey((k) => k + 1)
    setAssistModalOpen(true)
  }

  const closeAssistantModal = () => {
    setAssistModalOpen(false)
    setAssistEditingId(null)
    setAssistSaving(false)
    setAssistPendingProposal(null)
    setAssistCreateManual(false)
    setCatalogPickerOpen(false)
  }

  const onCreateAssistantFromWizard = async () => {
    if (!assistPendingProposal) {
      notifications.show({ color: 'yellow', message: 'Generate a proposal first.' })
      return
    }
    if (!canManageAssistants) {
      notifications.show({ color: 'yellow', message: 'Hosted assistants are not available in this mode.' })
      return
    }
    if (!assistLlm.trim()) {
      notifications.show({ color: 'yellow', message: 'Pick a model.' })
      return
    }
    const p = assistPendingProposal
    const tools: string[] = []
    const greeting = p.greeting?.trim() || 'Hello! How can I help?'
    const llm = assistLlm.trim()

    setAssistSaving(true)
    try {
      if (browserReady && auth.bundle && auth.workspaceId) {
        const uds = auth.bundle.client.owlbertHosted.assistants
        const wid = auth.workspaceId
        const created = (await hostedAssistantsCreate(uds, {
          workspaceId: wid,
          input: {
            workspace_id: null,
            name: p.name.trim(),
            description: p.description,
            instructions: p.instructions,
            greeting,
            llm,
            tools,
            memory: false,
            use_memory: null,
            use_history: null,
            chat_history_config: null,
            use_custom_llm: null,
            mcp_sources: [],
          },
        })) as { id?: string }
        const newId = typeof created?.id === 'string' ? created.id : null
        notifications.show({ color: 'green', message: 'Assistant created.' })
        await loadAssistantsBrowser()
        if (newId) setSelectedAssistantId(newId)
        closeAssistantModal()
        return
      }

      if (assistantsMeta.storage === 'hosted') {
        const out = await createAssistant({
          name: p.name.trim(),
          description: p.description,
          instructions: p.instructions,
          greeting,
          llm,
          tools,
        })
        const created = out.assistant as { id?: string }
        const newId = typeof created?.id === 'string' ? created.id : null
        notifications.show({ color: 'green', message: 'Assistant created.' })
        await reloadAssistantsBff()
        if (newId) setSelectedAssistantId(newId)
        closeAssistantModal()
        return
      }

      notifications.show({ color: 'yellow', message: 'Cannot save assistants in this mode.' })
    } catch (e) {
      notifications.show({ color: 'red', title: 'Create failed', message: String(e) })
    } finally {
      setAssistSaving(false)
    }
  }

  const onSaveAssistantModal = async () => {
    if (!assistName.trim()) {
      notifications.show({ color: 'yellow', message: 'Assistant name is required.' })
      return
    }
    if (!assistLlm.trim()) {
      notifications.show({ color: 'yellow', message: 'Pick a model.' })
      return
    }
    if (!canManageAssistants) {
      notifications.show({ color: 'yellow', message: 'Hosted assistants are not available in this mode.' })
      return
    }

    const tools = parseAssistantsToolsCsv(assistTools)
    const payload = {
      name: assistName.trim(),
      description: assistDescription,
      instructions: assistInstructions,
      greeting: assistGreeting,
      llm: assistLlm.trim(),
      tools,
    }

    setAssistSaving(true)
    try {
      if (browserReady && auth.bundle && auth.workspaceId) {
        const uds = auth.bundle.client.owlbertHosted.assistants
        const wid = auth.workspaceId
        if (assistModalMode === 'create') {
          const created = (await hostedAssistantsCreate(uds, {
            workspaceId: wid,
            input: {
              workspace_id: null,
              name: payload.name,
              description: payload.description,
              instructions: payload.instructions,
              greeting: payload.greeting,
              llm: payload.llm,
              tools,
              memory: false,
              use_memory: null,
              use_history: null,
              chat_history_config: null,
              use_custom_llm: null,
              mcp_sources: [],
            },
          })) as { id?: string }
          const newId = typeof created?.id === 'string' ? created.id : null
          notifications.show({ color: 'green', message: 'Assistant created.' })
          await loadAssistantsBrowser()
          if (newId) setSelectedAssistantId(newId)
        } else if (assistEditingId) {
          await hostedAssistantsUpdate(uds, {
            workspaceId: wid,
            input: {
              id: assistEditingId,
              name: payload.name,
              description: payload.description,
              instructions: payload.instructions,
              greeting: payload.greeting,
              llm: payload.llm,
              tools,
            },
          })
          notifications.show({ color: 'green', message: 'Assistant updated.' })
          await loadAssistantsBrowser()
        }
        closeAssistantModal()
        return
      }

      if (assistantsMeta.storage === 'hosted') {
        if (assistModalMode === 'create') {
          const out = await createAssistant(payload)
          const created = out.assistant as { id?: string }
          const newId = typeof created?.id === 'string' ? created.id : null
          notifications.show({ color: 'green', message: 'Assistant created.' })
          await reloadAssistantsBff()
          if (newId) setSelectedAssistantId(newId)
        } else if (assistEditingId) {
          await updateAssistant(assistEditingId, payload)
          notifications.show({ color: 'green', message: 'Assistant updated.' })
          await reloadAssistantsBff()
        }
        closeAssistantModal()
        return
      }

      notifications.show({ color: 'yellow', message: 'Cannot save assistants in this mode.' })
    } catch (e) {
      notifications.show({ color: 'red', title: 'Save failed', message: String(e) })
    } finally {
      setAssistSaving(false)
    }
  }

  const loadSessionsBrowser = React.useCallback(async () => {
    if (!browserReady || !auth.bundle || !auth.workspaceId) return
    const meta = await hostedChatSessionMetadataGetForWorkspace(auth.bundle.client.owlbertHosted.chatSessionMetadata, {
      workspaceId: auth.workspaceId,
    })
    const mapped = (meta as SessionRow[]).slice().sort((a, b) => {
      const tb = b.updatedAt ?? 0
      const ta = a.updatedAt ?? 0
      if (tb !== ta) return tb - ta
      return b.sessionId.localeCompare(a.sessionId)
    })
    setSessions(mapped)
    setSessionsLoaded(true)
  }, [auth.bundle, auth.workspaceId, browserReady])

  const loadMessagesBrowser = React.useCallback(
    async (sessionId: string) => {
      if (!browserReady || !auth.bundle || !auth.workspaceId) return
      const rows = await hostedChatMessagesGetForSession(auth.bundle.client.owlbertHosted.chatMessages, {
        workspaceId: auth.workspaceId,
        sessionId,
      })
      setMessages(rows as unknown as ChatRow[])
    },
    [auth.bundle, auth.workspaceId, browserReady],
  )

  React.useEffect(() => {
    if (!browserReady) return
    void loadAssistantsBrowser().catch((e) =>
      notifications.show({ color: 'red', title: 'Assistants', message: String(e) }),
    )
  }, [browserReady, loadAssistantsBrowser])

  React.useEffect(() => {
    if (!browserReady) return
    void loadSessionsBrowser().catch((e) =>
      notifications.show({ color: 'red', title: 'Sessions', message: String(e) }),
    )
  }, [browserReady, loadSessionsBrowser])

  // When the selected session changes (including on page reload / deep link), restore the
  // assistant that was last used in that session so the header picker and onSend are correct.
  React.useEffect(() => {
    if (!selectedSessionId || !sessions.length || !assistants.length) return
    const session = sessions.find((s) => s.sessionId === selectedSessionId)
    // Prefer the stable bound assistant id; fall back to last-used name for legacy
    // sessions created before session<->assistant binding existed.
    const boundId = (session as { assistantId?: string | null } | undefined)?.assistantId?.trim()
    if (boundId) {
      const boundMatch = assistants.find((a) => a.id === boundId)
      if (boundMatch) {
        setSelectedAssistantId(boundMatch.id)
        return
      }
    }
    const name = session?.lastAssistantName?.trim()
    if (!name) return
    const match = assistants.find((a) => a.name === name)
    if (match) setSelectedAssistantId(match.id)
  }, [selectedSessionId, sessions, assistants])

  React.useEffect(() => {
    if (!browserReady || !selectedSessionId) {
      setMessages([])
      return
    }
    void loadMessagesBrowser(selectedSessionId).catch((e) =>
      notifications.show({ color: 'red', title: 'Messages', message: String(e) }),
    )
  }, [browserReady, loadMessagesBrowser, selectedSessionId])

  const messagesEndRef = React.useRef<HTMLDivElement>(null)
  /** Native scroll container for messages (avoids `scrollIntoView` moving the page on iOS). */
  const messagesScrollRef = React.useRef<HTMLDivElement>(null)

  const scrollChatToBottom = React.useCallback(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const viewport = messagesScrollRef.current
        if (viewport) {
          viewport.scrollTop = viewport.scrollHeight
          return
        }
        messagesEndRef.current?.scrollIntoView({ block: 'end', behavior: 'auto' })
      })
    })
  }, [])

  React.useLayoutEffect(() => {
    if (!browserReady) return
    scrollChatToBottom()
  }, [browserReady, messages, selectedSessionId, scrollChatToBottom])

  const selectedAssistant = assistants.find((a) => a.id === selectedAssistantId) ?? null
  const selectedSession = sessions.find((s) => s.sessionId === selectedSessionId) ?? null

  // Conversations scoped to the currently selected assistant. Prefer the stable
  // assistantId binding; fall back to the legacy lastAssistantName for pre-binding rows.
  const sessionsForSelectedAssistant = React.useMemo(
    () =>
      selectedAssistant
        ? sessions.filter(
            (s) =>
              s.assistantId === selectedAssistant.id ||
              (!s.assistantId && s.lastAssistantName === selectedAssistant.name),
          )
        : [],
    [sessions, selectedAssistant],
  )

  // Agent-focused: picking an assistant scopes the conversation list and opens that
  // assistant's most recent chat (or the first-run / empty state). It does NOT mutate
  // the currently open session's binding.
  const onPickAssistant = React.useCallback(
    (nextId: string) => {
      setSelectedAssistantId(nextId)
      const next = assistants.find((a) => a.id === nextId)
      if (!next) return
      const scoped = sessions.filter(
        (s) => s.assistantId === next.id || (!s.assistantId && s.lastAssistantName === next.name),
      )
      if (scoped.length > 0) {
        setSelectedSessionId(scoped[0].sessionId)
        navigate(`/assistants/${scoped[0].sessionId}`)
      } else {
        setSelectedSessionId(null)
        navigate('/assistants')
      }
    },
    [assistants, sessions, navigate],
  )

  const onPickConversation = React.useCallback(
    (value: string | null) => {
      if (!value) return
      if (value === '__new__') {
        void onNewChat()
        return
      }
      setSelectedSessionId(value)
      navigate(`/assistants/${value}`)
    },
    [navigate],
  )

  const onNewChat = async () => {
    if (!browserReady || !auth.bundle || !auth.workspaceId) {
      notifications.show({ color: 'yellow', message: 'Sign in on Settings and pick a workspace first.' })
      return
    }
    if (!selectedAssistant) {
      notifications.show({ color: 'yellow', message: 'Pick an assistant first.' })
      return
    }
    const sessionId = crypto.randomUUID()
    try {
      await hostedChatSessionMetadataUpsertMetadata(auth.bundle.client.owlbertHosted.chatSessionMetadata, {
        workspaceId: auth.workspaceId,
        sessionId,
        partial: {
          displayName: 'New chat',
          lastAssistantName: selectedAssistant.name,
          assistantId: selectedAssistant.id,
        },
      })
      await loadSessionsBrowser()
      setSelectedSessionId(sessionId)
      setMessages([])
      navigate(`/assistants/${sessionId}`)
    } catch (e) {
      notifications.show({ color: 'red', title: 'New chat failed', message: String(e) })
    }
  }

  /**
   * First run: a brand-new workspace has no sessions, and the composer is disabled until one is
   * selected — so the user lands on a chat screen they cannot type into and has to discover
   * "New chat" first. Open a chat for them once assistants and sessions have both loaded.
   *
   * Guarded per workspace so this fires once and never fights a user who deletes their last chat
   * in the same visit (the ref is only reset when the workspace changes).
   */
  const autoCreatedForWorkspaceRef = React.useRef<string | null>(null)
  React.useEffect(() => {
    if (!browserReady || !auth.workspaceId) return
    if (!sessionsLoaded || sessions.length > 0) return
    if (!selectedAssistant || selectedSessionId) return
    if (autoCreatedForWorkspaceRef.current === auth.workspaceId) return
    autoCreatedForWorkspaceRef.current = auth.workspaceId
    void onNewChat()
    // onNewChat is re-created every render; the workspace ref guard is what keeps this to one run.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [browserReady, auth.workspaceId, sessionsLoaded, sessions.length, selectedAssistant, selectedSessionId])

  // Re-arm the first-run check when the user switches workspaces (each workspace gets its own chat).
  React.useEffect(() => {
    setSessionsLoaded(false)
  }, [auth.workspaceId])

  const onRenameSession = async (sessionId: string, nextName: string) => {
    const trimmed = nextName.trim()
    if (!browserReady || !auth.bundle || !auth.workspaceId || !trimmed) return
    try {
      await hostedChatSessionMetadataSetDisplayName(auth.bundle.client.owlbertHosted.chatSessionMetadata, {
        workspaceId: auth.workspaceId,
        sessionId,
        name: trimmed,
      })
      await loadSessionsBrowser()
    } catch (e) {
      notifications.show({ color: 'red', title: 'Rename failed', message: String(e) })
    }
  }

  const onDeleteSession = async (sessionId: string) => {
    if (!browserReady || !auth.bundle || !auth.workspaceId) return
    try {
      await hostedChatSessionMetadataDeleteSession(auth.bundle.client.owlbertHosted.chatSessionMetadata, {
        workspaceId: auth.workspaceId,
        sessionId,
      })
      if (selectedSessionId === sessionId) {
        setSelectedSessionId(null)
        setMessages([])
        if (sessionIdFromUrl === sessionId) navigate('/assistants')
      }
      await loadSessionsBrowser()
      notifications.show({ color: 'gray', message: 'Chat deleted.' })
    } catch (e) {
      notifications.show({ color: 'red', title: 'Delete failed', message: String(e) })
    }
  }

  const onSend = async (text: string, attachments: ChatAttachmentDraft[] = []) => {
    if (!browserReady || !auth.bundle || !auth.workspaceId) return
    if (!selectedSessionId) {
      notifications.show({ color: 'yellow', message: 'Create or select a chat first.' })
      return
    }
    if (!selectedAssistant) {
      notifications.show({ color: 'yellow', message: 'Pick an assistant first.' })
      return
    }
    if (!text && !attachments.length) return
    setSending(true)
    cancelRef.current = false
    webLog('info', 'assistants-page', 'onSend start', {
      sessionId: selectedSessionId,
      assistantId: selectedAssistant.id,
      draftChars: text.length,
    })
    try {
      const procedures = auth.bundle.client.owlbertHosted.chatMessages

      // Attachments: photos become session attachments (rendered inline + sent to the
      // model as vision parts); documents are saved to the library, indexed for KB
      // search, and their extracted text is inlined into this turn only.
      const images: PreparedChatImageAttachment[] = []
      const documents: PreparedChatDocumentAttachment[] = []
      const attachmentMarkdown: string[] = []
      for (const draft of attachments) {
        const prepared = await prepareChatAttachment(draft.file)
        if ('error' in prepared) {
          notifications.show({ color: 'yellow', title: 'Attachment skipped', message: `${prepared.name}: ${prepared.error}` })
          continue
        }
        if (prepared.kind === 'image') {
          images.push(prepared)
          try {
            const attachmentId = await hostedSessionAttachmentsAdd(auth.bundle.client.owlbertHosted.sessionAttachments, {
              workspaceId: auth.workspaceId,
              chatSessionId: selectedSessionId,
              name: prepared.name,
              mimeType: prepared.mimeType,
              content: prepared.base64,
            })
            attachmentMarkdown.push(formatOwlbertAttachmentImageMarkdown(attachmentId, prepared.name))
          } catch (e) {
            webLog('warn', 'assistants-page', 'session attachment add failed', { message: String(e) })
          }
          continue
        }
        documents.push(prepared)
        try {
          const docId = crypto.randomUUID()
          const metadata: Record<string, unknown> = {
            sourceFilename: prepared.name,
            sizeBytes: draft.file.size,
            uploadedAt: new Date().toISOString(),
          }
          await hostedUserDocumentsAdd(auth.bundle.client.owlbertHosted.userDocuments, {
            workspaceId: auth.workspaceId,
            request: {
              id: docId,
              name: prepared.title,
              content: prepared.text.trim() || ' ',
              metadata,
              mimeType: prepared.mimeType,
              blob: prepared.blob ?? null,
            },
          })
          void indexBrowserHostedUserDocumentToKb({
            clients: { embeddings: auth.bundle.client.embeddings, knowledgebase: auth.bundle.client.knowledgebase },
            workspaceId: auth.workspaceId,
            doc: {
              id: docId,
              name: prepared.title,
              content: prepared.text,
              metadata: { sourceFilename: prepared.name },
              mimeType: prepared.mimeType,
            },
          }).catch((e) => webLog('warn', 'kb-index', 'chat attachment index failed', { message: String(e) }))
          attachmentMarkdown.push(`📎 **${prepared.name}** — saved to your library`)
        } catch (e) {
          webLog('warn', 'assistants-page', 'attachment library save failed', { message: String(e) })
          attachmentMarkdown.push(`📎 **${prepared.name}** (attached to this message)`)
        }
      }

      const userContent = [text, ...attachmentMarkdown].filter(Boolean).join('\n\n')
      const userId = crypto.randomUUID()
      await hostedChatMessagesUpsertMessage(procedures, {
        workspaceId: auth.workspaceId,
        sessionId: selectedSessionId,
        id: userId,
        role: 'user',
        content: userContent,
      })
      const latest = await hostedChatMessagesGetForSession(procedures, {
        workspaceId: auth.workspaceId,
        sessionId: selectedSessionId,
      })
      const rows = latest as unknown as ChatRow[]
      setMessages(rows)

      const lmMessages: WebChatAutoMessage[] = rows
        .filter((m) => m.role === 'user' || m.role === 'assistant' || m.role === 'system')
        .map((m) => ({ role: m.role as 'user' | 'assistant' | 'system', content: m.content }))

      // Attach document text + image parts to the turn we just sent (never persisted).
      const lastTurn = lmMessages[lmMessages.length - 1]
      if (lastTurn && lastTurn.role === 'user') {
        const documentContext = formatDocumentAttachmentsForModel(documents)
        if (documentContext) lastTurn.content = `${lastTurn.content}${documentContext}`
        if (images.length) {
          lastTurn.content_parts = [
            ...(lastTurn.content ? [{ type: 'text' as const, text: lastTurn.content }] : []),
            ...images.map((img) => ({
              type: 'image' as const,
              source: { type: 'base64' as const, media_type: img.mimeType, data: img.base64 },
            })),
          ]
        }
      }

      const assistant = await auth.bundle.client.owlbertHosted.assistants.get.query({
        workspaceId: auth.workspaceId,
        id: selectedAssistant.id,
      })
      const system =
        (assistant as { instructions?: string; description?: string } | null)?.instructions ||
        (assistant as { instructions?: string; description?: string } | null)?.description ||
        ''
      const assistantToolIds = Array.isArray((assistant as { tools?: string[] } | null)?.tools)
        ? ((assistant as { tools?: string[] }).tools as string[])
        : []

      const reply = await runWebChatAutoWithKbTool({
        clients: {
          llm: auth.bundle.client.llm,
          embeddings: auth.bundle.client.embeddings,
          knowledgebase: auth.bundle.client.knowledgebase,
          owlbertHostedUserDocuments: auth.bundle.client.owlbertHosted.userDocuments,
          images: auth.bundle.client.images,
          sessionAttachments: auth.bundle.client.owlbertHosted.sessionAttachments,
        },
        workspaceId: auth.workspaceId,
        sessionId: selectedSessionId,
        chatSessionMetadata: auth.bundle.client.owlbertHosted.chatSessionMetadata,
        messages: lmMessages,
        system_prompt: system || undefined,
        temperature: 0.4,
        assistantToolIds,
        shouldCancel: () => cancelRef.current,
      })

      if (cancelRef.current || reply.cancelled) {
        const refreshed = await hostedChatMessagesGetForSession(procedures, {
          workspaceId: auth.workspaceId,
          sessionId: selectedSessionId,
        })
        setMessages(refreshed as unknown as ChatRow[])
        notifications.show({ color: 'gray', message: 'Generation stopped.' })
        return
      }

      const body = reply.content ?? ''

      const assistantMsgId = crypto.randomUUID()
      await hostedChatMessagesUpsertMessage(procedures, {
        workspaceId: auth.workspaceId,
        sessionId: selectedSessionId,
        id: assistantMsgId,
        role: 'assistant',
        content: body,
        finishReason: reply.finishReason ?? null,
        assistantName: selectedAssistant.name,
      })
      const after = await hostedChatMessagesGetForSession(procedures, {
        workspaceId: auth.workspaceId,
        sessionId: selectedSessionId,
      })
      setMessages(after as unknown as ChatRow[])
      await hostedChatSessionMetadataUpsertMetadata(auth.bundle.client.owlbertHosted.chatSessionMetadata, {
        workspaceId: auth.workspaceId,
        sessionId: selectedSessionId,
        partial: {
          lastAssistantName: selectedAssistant.name,
        },
      })
      await loadSessionsBrowser()

      // Background: give brand-new "New chat" threads a real title without blocking the
      // turn. No-ops if the model already named the thread via set_chat_display_name.
      const convoForTitle = (after as unknown as ChatRow[])
        .filter((m) => m.role === 'user' || m.role === 'assistant')
        .map((m) => ({ role: m.role as 'user' | 'assistant', content: m.content }))
      void maybeAutoTitleWebChatSession({
        llm: auth.bundle.client.llm,
        chatSessionMetadata: auth.bundle.client.owlbertHosted.chatSessionMetadata,
        workspaceId: auth.workspaceId,
        sessionId: selectedSessionId,
        currentDisplayName: selectedSession?.displayName ?? null,
        conversation: convoForTitle,
      })
        .then((renamed) => (renamed ? loadSessionsBrowser() : undefined))
        .catch(() => {})

      webLog('info', 'assistants-page', 'onSend complete', { sessionId: selectedSessionId, replyChars: body.length })
    } catch (e) {
      webLog('error', 'assistants-page', 'onSend failed', { message: String(e) })
      notifications.show({ color: 'red', title: 'Send failed', message: String(e) })
    } finally {
      setSending(false)
    }
  }

  const onRegenerate = async () => {
    if (!browserReady || !auth.bundle || !auth.workspaceId) return
    if (!selectedSessionId || !selectedAssistant) return
    let lastAssistantIdx = -1
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant') {
        lastAssistantIdx = i
        break
      }
    }
    if (lastAssistantIdx < 0) return
    const target = messages[lastAssistantIdx]
    const history = messages.slice(0, lastAssistantIdx)
    if (!history.some((m) => m.role === 'user')) return
    setSending(true)
    cancelRef.current = false
    try {
      const procedures = auth.bundle.client.owlbertHosted.chatMessages
      await hostedChatMessagesDeleteMessage(procedures, {
        workspaceId: auth.workspaceId,
        sessionId: selectedSessionId,
        messageId: target.id,
      })
      setMessages(history)

      const lmMessages = history
        .filter((m) => m.role === 'user' || m.role === 'assistant' || m.role === 'system')
        .map((m) => ({ role: m.role as 'user' | 'assistant' | 'system', content: m.content }))

      const assistant = await auth.bundle.client.owlbertHosted.assistants.get.query({
        workspaceId: auth.workspaceId,
        id: selectedAssistant.id,
      })
      const system =
        (assistant as { instructions?: string; description?: string } | null)?.instructions ||
        (assistant as { instructions?: string; description?: string } | null)?.description ||
        ''
      const assistantToolIds = Array.isArray((assistant as { tools?: string[] } | null)?.tools)
        ? ((assistant as { tools?: string[] }).tools as string[])
        : []

      const reply = await runWebChatAutoWithKbTool({
        clients: {
          llm: auth.bundle.client.llm,
          embeddings: auth.bundle.client.embeddings,
          knowledgebase: auth.bundle.client.knowledgebase,
          owlbertHostedUserDocuments: auth.bundle.client.owlbertHosted.userDocuments,
          images: auth.bundle.client.images,
          sessionAttachments: auth.bundle.client.owlbertHosted.sessionAttachments,
        },
        workspaceId: auth.workspaceId,
        sessionId: selectedSessionId,
        chatSessionMetadata: auth.bundle.client.owlbertHosted.chatSessionMetadata,
        messages: lmMessages,
        system_prompt: system || undefined,
        temperature: 0.4,
        assistantToolIds,
        shouldCancel: () => cancelRef.current,
      })

      if (cancelRef.current || reply.cancelled) {
        const refreshed = await hostedChatMessagesGetForSession(procedures, {
          workspaceId: auth.workspaceId,
          sessionId: selectedSessionId,
        })
        setMessages(refreshed as unknown as ChatRow[])
        notifications.show({ color: 'gray', message: 'Generation stopped.' })
        return
      }

      const body = reply.content ?? ''
      await hostedChatMessagesUpsertMessage(procedures, {
        workspaceId: auth.workspaceId,
        sessionId: selectedSessionId,
        id: crypto.randomUUID(),
        role: 'assistant',
        content: body,
        finishReason: reply.finishReason ?? null,
        assistantName: selectedAssistant.name,
      })
      const after = await hostedChatMessagesGetForSession(procedures, {
        workspaceId: auth.workspaceId,
        sessionId: selectedSessionId,
      })
      setMessages(after as unknown as ChatRow[])
    } catch (e) {
      webLog('error', 'assistants-page', 'onRegenerate failed', { message: String(e) })
      notifications.show({ color: 'red', title: 'Regenerate failed', message: String(e) })
    } finally {
      setSending(false)
    }
  }

  const onContinue = async () => {
    if (!browserReady || !auth.bundle || !auth.workspaceId) return
    if (!selectedSessionId || !selectedAssistant) return
    const last = messages[messages.length - 1]
    if (!last || last.role !== 'assistant') return
    if (!isTruncatedAssistantFinishReason(last.finishReason)) return

    setContinuing(true)
    cancelRef.current = false
    webLog('info', 'assistants-page', 'onContinue start', {
      sessionId: selectedSessionId,
      messageId: last.id,
      finishReason: last.finishReason,
    })
    try {
      const procedures = auth.bundle.client.owlbertHosted.chatMessages
      const lmMessages = messages
        .filter((m) => m.role === 'user' || m.role === 'assistant' || m.role === 'system')
        .map((m) => ({ role: m.role as 'user' | 'assistant' | 'system', content: m.content }))
      lmMessages.push({ role: 'user', content: WEB_CHAT_CONTINUE_PROMPT })

      const assistant = await auth.bundle.client.owlbertHosted.assistants.get.query({
        workspaceId: auth.workspaceId,
        id: selectedAssistant.id,
      })
      const system =
        (assistant as { instructions?: string; description?: string } | null)?.instructions ||
        (assistant as { instructions?: string; description?: string } | null)?.description ||
        ''
      const assistantToolIds = Array.isArray((assistant as { tools?: string[] } | null)?.tools)
        ? ((assistant as { tools?: string[] }).tools as string[])
        : []

      const reply = await runWebChatAutoWithKbTool({
        clients: {
          llm: auth.bundle.client.llm,
          embeddings: auth.bundle.client.embeddings,
          knowledgebase: auth.bundle.client.knowledgebase,
          owlbertHostedUserDocuments: auth.bundle.client.owlbertHosted.userDocuments,
          images: auth.bundle.client.images,
          sessionAttachments: auth.bundle.client.owlbertHosted.sessionAttachments,
        },
        workspaceId: auth.workspaceId,
        sessionId: selectedSessionId,
        chatSessionMetadata: auth.bundle.client.owlbertHosted.chatSessionMetadata,
        messages: lmMessages,
        system_prompt: system || undefined,
        temperature: 0.4,
        assistantToolIds,
        shouldCancel: () => cancelRef.current,
      })

      if (cancelRef.current || reply.cancelled) {
        notifications.show({ color: 'gray', message: 'Generation stopped.' })
        return
      }

      const appended = reply.content ?? ''
      if (!appended.trim()) {
        notifications.show({ color: 'yellow', message: 'Nothing to continue.' })
        return
      }

      await hostedChatMessagesAppendToMessage(procedures, {
        workspaceId: auth.workspaceId,
        sessionId: selectedSessionId,
        messageId: last.id,
        appendedContent: appended,
        finishReason: reply.finishReason ?? null,
      })

      const after = await hostedChatMessagesGetForSession(procedures, {
        workspaceId: auth.workspaceId,
        sessionId: selectedSessionId,
      })
      setMessages(after as unknown as ChatRow[])
      webLog('info', 'assistants-page', 'onContinue complete', {
        sessionId: selectedSessionId,
        appendedChars: appended.length,
        finishReason: reply.finishReason,
      })
    } catch (e) {
      webLog('error', 'assistants-page', 'onContinue failed', { message: String(e) })
      notifications.show({ color: 'red', title: 'Continue failed', message: String(e) })
    } finally {
      setContinuing(false)
    }
  }

  const assistantFormFields = (
    <>
      <TextInput label="Name" required value={assistName} onChange={(e) => setAssistName(e.currentTarget.value)} />
      <AssistantModelField
        value={assistLlm}
        onChange={setAssistLlm}
        catalogReady={catalogReady}
        bundle={auth.bundle}
        disabled={false}
      />
      <Textarea label="Description" minRows={2} value={assistDescription} onChange={(e) => setAssistDescription(e.currentTarget.value)} />
      <Textarea
        label="Instructions"
        description="System prompt for this assistant."
        minRows={5}
        value={assistInstructions}
        onChange={(e) => setAssistInstructions(e.currentTarget.value)}
      />
      <Textarea label="Greeting" minRows={2} value={assistGreeting} onChange={(e) => setAssistGreeting(e.currentTarget.value)} />
      <TextInput
        label="Tools"
        description="Optional. Comma-separated tool ids; leave blank for defaults. Examples: search_knowledgebase, get_library_document, create_workspace_markdown_document, generate_image."
        value={assistTools}
        onChange={(e) => setAssistTools(e.currentTarget.value)}
        placeholder="Leave blank for defaults"
      />
    </>
  )

  const assistantEditorModal = (
    <Modal
      opened={assistModalOpen}
      onClose={closeAssistantModal}
      title={assistModalMode === 'create' ? 'New assistant' : 'Edit assistant'}
      size="lg"
    >
      {assistModalMode === 'create' && !assistCreateManual ? (
        <Stack gap="sm">
          <Text size="sm" c="dimmed">
            Describe what you want, pick a model assigned to your account, then generate a proposal or start from a
            template.
          </Text>
          <AssistantModelField
            value={assistLlm}
            onChange={setAssistLlm}
            catalogReady={catalogReady}
            bundle={auth.bundle}
            disabled={assistSaving}
          />
          <AssistantAutoCreatePanel
            key={assistWizardKey}
            browserReady={browserReady}
            bundle={auth.bundle}
            disabled={!canManageAssistants || assistSaving}
            onProposal={(p) => setAssistPendingProposal(p)}
          />
          {assistPendingProposal ? (
            <Paper withBorder p="sm" radius="md">
              <Text size="xs" tt="uppercase" fw={700} c="dimmed">
                Preview
              </Text>
              <Text fw={600} mt={6}>
                {assistPendingProposal.name}
              </Text>
              {assistPendingProposal.description ? (
                <Text size="sm" c="dimmed" mt={4}>
                  {assistPendingProposal.description}
                </Text>
              ) : null}
              <Text size="xs" c="dimmed" mt="sm" lineClamp={5} style={{ whiteSpace: 'pre-wrap' }}>
                {assistPendingProposal.instructions}
              </Text>
              <Button
                fullWidth
                mt="md"
                className="accent-button"
                loading={assistSaving}
                onClick={() => void onCreateAssistantFromWizard()}
              >
                Create assistant
              </Button>
            </Paper>
          ) : null}
          <Group justify="space-between" align="center" wrap="wrap" gap="sm" mt="xs">
            <Group gap="xs" wrap="wrap">
              <Button
                variant="light"
                size="sm"
                leftSection={<IconBooks size={16} />}
                disabled={!auth.bundle?.client || !canManageAssistants}
                onClick={() => setCatalogPickerOpen(true)}
              >
                Pick from catalog
              </Button>
              <Button variant="subtle" size="sm" onClick={() => setAssistCreateManual(true)}>
                Blank assistant — full form
              </Button>
            </Group>
            <Button variant="default" onClick={closeAssistantModal}>
              Cancel
            </Button>
          </Group>
        </Stack>
      ) : (
        <Stack gap="sm">
          {assistModalMode === 'create' && assistCreateManual ? (
            <Button variant="subtle" size="sm" w="fit-content" onClick={() => setAssistCreateManual(false)}>
              Back to AI setup
            </Button>
          ) : null}
          {assistantFormFields}
          <Group justify="flex-end" mt="xs">
            <Button variant="default" onClick={closeAssistantModal}>
              Cancel
            </Button>
            <Button className="accent-button" loading={assistSaving} onClick={() => void onSaveAssistantModal()}>
              Save
            </Button>
          </Group>
        </Stack>
      )}
    </Modal>
  )

  if (!browserReady) {
    const selected = assistants[0] ?? null
    return (
      <>
        <Flex direction="row" className="owlbert-web-chat-root">
        <Box className="owlbert-web-chat-sidebar">
          <Group justify="space-between" px="md" py="sm" wrap="nowrap">
            <Title order={5} style={{ flex: 1 }}>
              Chats
            </Title>
            <Button size="xs" variant="light" leftSection={<IconMessageCircle size={16} />} disabled>
              New
            </Button>
          </Group>
          <Divider />
          <ScrollArea style={{ flex: 1 }} type="auto" offsetScrollbars>
            <Stack gap={6} p="sm">
              {assistantsMeta.storage === 'hosted' && assistantsMeta.message ? (
                <Paper withBorder p="sm" radius="md">
                  <Text size="xs" c="red">
                    {assistantsMeta.message}
                    {assistantsMeta.error ? ` (${assistantsMeta.error})` : ''}
                  </Text>
                </Paper>
              ) : null}
              {assistantsMeta.storage === 'none' ? (
                <Paper withBorder p="sm" radius="md" style={{ cursor: 'default' }}>
                  <Text size="sm" fw={600}>
                    Welcome
                  </Text>
                  <Text size="xs" c="dimmed" lineClamp={6}>
                    Sign in on <Link to="/settings">Settings</Link> to use cloud chat and assistants.
                  </Text>
                </Paper>
              ) : (
                <Paper withBorder p="sm" radius="md">
                  <Text size="sm" fw={600}>
                    Chats
                  </Text>
                  <Text size="xs" c="dimmed" mt={4} lineClamp={6}>
                    Sign in to see recent chats here. Manage assistants on{' '}
                    <Link to="/settings/assistants">Settings → Assistants</Link>.
                  </Text>
                </Paper>
              )}
            </Stack>
          </ScrollArea>
        </Box>

        <Flex direction="column" className="owlbert-web-chat-main">
          <Box className="owlbert-web-chat-main-header">
            <Title order={4}>{selected ? selected.name : 'Welcome'}</Title>
            <Text size="sm" c="dimmed">
              {selected ? `${selected.llm}` : 'Sign in to use chat'}
            </Text>
          </Box>
          <ScrollArea style={{ flex: 1 }} p="lg" type="auto" offsetScrollbars>
            <Center style={{ minHeight: 220 }}>
              <Stack align="center" gap="sm" maw={520}>
                <IconRobot size={56} stroke={1.25} />
                <Text ta="center" fw={600}>
                  Browser session not active
                </Text>
                <Text ta="center" size="sm" c="dimmed">
                  Sign in on Settings to use cloud chat.
                </Text>
              </Stack>
            </Center>
          </ScrollArea>
          <Box className="owlbert-web-chat-composer">
            <Group align="flex-end" wrap="nowrap" gap="sm">
              <TextInput style={{ flex: 1 }} placeholder="Message assistant…" disabled radius="md" size="md" />
              <Button radius="md" disabled>
                Send
              </Button>
            </Group>
          </Box>
        </Flex>
      </Flex>
        {assistantEditorModal}
      </>
    )
  }

  const sessionListSidebar = (
    <Box
      className="owlbert-web-chat-sidebar"
      style={
        isMobileChatStack
          ? { width: '100%', maxWidth: '100%', minWidth: 0, borderRight: 'none', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }
          : undefined
      }
    >
      <Group justify="space-between" px="md" py="sm" wrap="nowrap">
        <Title order={5} style={{ flex: 1 }}>
          Assistants
        </Title>
        {canManageAssistants ? (
          <ActionIcon variant="light" size="lg" aria-label="Add assistant" onClick={() => openCreateAssistantModal()}>
            <IconUserPlus size={16} />
          </ActionIcon>
        ) : null}
      </Group>
      <Divider />
      <ScrollArea style={{ flex: 1 }} type="auto" offsetScrollbars>
        <Stack gap={6} p="sm">
          {assistants.length === 0 ? (
            <Paper withBorder p="md" radius="md">
              <Stack gap="sm">
                <Text size="sm" fw={600}>
                  Add an assistant to get started
                </Text>
                <Text size="xs" c="dimmed">
                  Chats are tied to an assistant. Create one with the AI wizard, pick a starter from the catalog, or
                  use the advanced blank form — then you can start chatting.
                </Text>
                <Button
                  className="accent-button"
                  size="sm"
                  leftSection={<IconUserPlus size={16} />}
                  onClick={() => openCreateAssistantModal()}
                >
                  Add assistant
                </Button>
              </Stack>
            </Paper>
          ) : (
            assistants.map((a) => {
              const isActive = selectedAssistantId === a.id
              const convoCount = sessions.filter(
                (s) => s.assistantId === a.id || (!s.assistantId && s.lastAssistantName === a.name),
              ).length
              return (
                <Paper
                  key={a.id}
                  withBorder
                  p="sm"
                  radius="md"
                  style={{
                    cursor: 'pointer',
                    outline: isActive ? '2px solid var(--mantine-color-grape-filled)' : undefined,
                  }}
                  onClick={() => onPickAssistant(a.id)}
                >
                  <Group gap="sm" wrap="nowrap" align="center">
                    <Avatar radius="xl" size="sm" color="grape">
                      {a.name.slice(0, 1).toUpperCase()}
                    </Avatar>
                    <Box style={{ flex: 1, minWidth: 0 }}>
                      <Text size="sm" fw={600} lineClamp={1}>
                        {a.name}
                      </Text>
                      <Text size="xs" c="dimmed" lineClamp={1}>
                        {convoCount === 0 ? 'No chats yet' : `${convoCount} chat${convoCount === 1 ? '' : 's'}`}
                      </Text>
                    </Box>
                  </Group>
                </Paper>
              )
            })
          )}
        </Stack>
      </ScrollArea>
    </Box>
  )

  // Conversation picker — the center column title is the dropdown trigger.
  const conversationPicker = selectedAssistant ? (
    <Menu position="bottom-start" withinPortal shadow="md" width={300}>
      <Menu.Target>
        <Group
          gap={6}
          wrap="nowrap"
          align="center"
          style={{ cursor: 'pointer', minWidth: 0, maxWidth: '100%' }}
          role="button"
          aria-label="Switch conversation"
        >
          <Title order={4} lineClamp={1} style={{ minWidth: 0 }}>
            {selectedSession ? selectedSession.displayName || 'Untitled' : 'New conversation'}
          </Title>
          <IconChevronDown size={18} stroke={1.5} style={{ flexShrink: 0, opacity: 0.6 }} />
        </Group>
      </Menu.Target>
      <Menu.Dropdown>
        <Menu.Item leftSection={<IconMessageCircle size={14} />} onClick={() => void onNewChat()}>
          New conversation
        </Menu.Item>
        {sessionsForSelectedAssistant.length > 0 ? <Menu.Divider /> : null}
        <ScrollArea.Autosize mah={340} type="auto">
          {sessionsForSelectedAssistant.map((s) => (
            <Group
              key={s.sessionId}
              gap={2}
              wrap="nowrap"
              px="xs"
              py={4}
              style={{
                borderRadius: 6,
                background:
                  selectedSessionId === s.sessionId ? 'var(--mantine-color-grape-light)' : undefined,
              }}
            >
              <Box
                style={{ flex: 1, minWidth: 0, cursor: 'pointer' }}
                onClick={() => onPickConversation(s.sessionId)}
              >
                <Text size="sm" lineClamp={1}>
                  {s.displayName || 'Untitled'}
                </Text>
              </Box>
              <ActionIcon
                variant="subtle"
                color="gray"
                size="sm"
                aria-label="Rename conversation"
                onClick={(e) => {
                  e.stopPropagation()
                  setRenameTarget(s)
                  setRenameValue(s.displayName || '')
                }}
              >
                <IconPencil size={14} />
              </ActionIcon>
              <ActionIcon
                variant="subtle"
                color="red"
                size="sm"
                aria-label="Delete conversation"
                onClick={(e) => {
                  e.stopPropagation()
                  setDeleteTarget(s)
                }}
              >
                <IconTrash size={14} />
              </ActionIcon>
            </Group>
          ))}
        </ScrollArea.Autosize>
      </Menu.Dropdown>
    </Menu>
  ) : null

  const desktopChatHeader = (
    <Box className="owlbert-web-chat-main-header">
      <Group justify="space-between" align="center" wrap="nowrap" gap="sm">
        <Box style={{ minWidth: 0, flex: 1 }}>
          {conversationPicker ?? (
            <Title order={4} lineClamp={1}>
              {selectedSession ? selectedSession.displayName || 'Untitled' : 'Chat'}
            </Title>
          )}
        </Box>
      </Group>
    </Box>
  )

  const mobileChatHeader = (
    <Box
      className="owlbert-web-mobile-stack-header"
      style={{
        flexShrink: 0,
        padding: '8px 12px',
        paddingTop: 'calc(8px + env(safe-area-inset-top, 0px))',
        borderBottom: '1px solid var(--separator-color)',
        backgroundColor: 'var(--shell-main-bg)',
      }}
    >
      <Group gap="sm" wrap="nowrap" align="center">
        <ActionIcon
          variant="default"
          size="lg"
          radius="xl"
          aria-label="Back to chats"
          onClick={() => navigate('/assistants')}
        >
          <IconArrowLeft size={20} stroke={1.5} />
        </ActionIcon>
        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
          <Title order={5} lineClamp={2}>
            {selectedSession ? selectedSession.displayName || 'Untitled' : 'Chat'}
          </Title>
        </Stack>
        {assistants.length > 0 ? (
          <Select
            size="xs"
            placeholder="Assistant"
            aria-label="Assistant"
            data={assistants.map((a) => ({ value: a.id, label: a.name }))}
            value={selectedAssistantId ?? undefined}
            onChange={(id) => {
              if (id) onPickAssistant(id)
            }}
            disabled={sending}
            w={150}
            searchable
            clearable={false}
          />
        ) : null}
      </Group>
    </Box>
  )

  const chatMessagesAndComposer = (
    <>
      <Box
        ref={messagesScrollRef}
        className="owlbert-web-chat-messages-viewport"
        p="lg"
        style={{ flex: 1, minHeight: 0 }}
      >
        <Stack gap="xl" maw={760} mx="auto" w="100%">
          {messages.length === 0 ? (
            <Center style={{ minHeight: 240 }}>
              <Stack gap="sm" align="center" maw={520} px="md">
                <Avatar color="grape" radius="xl" size="lg">
                  {(selectedAssistant?.name || 'A').slice(0, 1).toUpperCase()}
                </Avatar>
                <Title order={4} ta="center">
                  {selectedAssistant?.name || 'Start a conversation'}
                </Title>
                <Text c="dimmed" size="sm" ta="center" style={{ whiteSpace: 'pre-wrap' }}>
                  {selectedAssistant?.greeting?.trim() ||
                    selectedAssistant?.description?.trim() ||
                    'Send a message below to get started.'}
                </Text>
                <Group gap="xs" justify="center" wrap="wrap" mt="xs">
                  {STARTER_PROMPTS.map((p) => (
                    <Button
                      key={p}
                      size="xs"
                      variant="light"
                      radius="xl"
                      disabled={!selectedSessionId || sending}
                      onClick={() => void onSend(p)}
                    >
                      {p}
                    </Button>
                  ))}
                </Group>
              </Stack>
            </Center>
          ) : (
            messages.map((m, idx) => {
              const { primary, thinking } = splitThinkingFromChatContent(m.content)
              const thinkingTrimmed = thinking.trim()
              const isLast = idx === messages.length - 1
              const showMessageContinue =
                isLast &&
                m.role === 'assistant' &&
                !sending &&
                isTruncatedAssistantFinishReason(m.finishReason)
              const isUser = m.role === 'user'
              const prevAssistantName = (() => {
                for (let i = idx - 1; i >= 0; i--) {
                  if (messages[i].role === 'assistant') return messages[i].assistantName || null
                }
                return null
              })()
              const thisAssistantName = m.role === 'assistant' ? m.assistantName || null : null
              const showSwitchDivider =
                m.role === 'assistant' &&
                Boolean(thisAssistantName) &&
                Boolean(prevAssistantName) &&
                thisAssistantName !== prevAssistantName
              const labelName =
                m.role === 'user'
                  ? 'You'
                  : m.role === 'assistant'
                    ? m.assistantName || selectedAssistant?.name || 'Assistant'
                    : m.role
              const messageTime = formatMessageTime(m.timestamp)
              const messageBody = (
                <>
                  <Group justify="space-between" gap="xs" align="center" wrap="nowrap" mb={2}>
                    <Group gap="xs" align="center" wrap="nowrap" style={{ minWidth: 0 }}>
                      <Text size="xs" c="dimmed" tt="uppercase" fw={700} truncate>
                        {labelName}
                      </Text>
                      {messageTime ? (
                        <Text size="xs" c="dimmed">
                          {messageTime}
                        </Text>
                      ) : null}
                    </Group>
                    {m.role === 'assistant' ? (
                      <Group gap={4} wrap="nowrap">
                        {isLast && !sending && !continuing ? (
                          <Tooltip label="Regenerate" withArrow position="left">
                            <ActionIcon
                              size="sm"
                              variant="subtle"
                              color="gray"
                              aria-label="Regenerate reply"
                              onClick={() => void onRegenerate()}
                            >
                              <IconRefresh size={14} />
                            </ActionIcon>
                          </Tooltip>
                        ) : null}
                        {primary.trim() ? (
                          <CopyButton value={primary} timeout={1500}>
                            {({ copied, copy }) => (
                              <Tooltip label={copied ? 'Copied' : 'Copy'} withArrow position="left">
                                <ActionIcon
                                  size="sm"
                                  variant="subtle"
                                  color={copied ? 'teal' : 'gray'}
                                  onClick={copy}
                                  aria-label="Copy message"
                                >
                                  {copied ? <IconCheck size={14} /> : <IconCopy size={14} />}
                                </ActionIcon>
                              </Tooltip>
                            )}
                          </CopyButton>
                        ) : null}
                      </Group>
                    ) : null}
                  </Group>
                  <ChatMessageContent content={primary} loadAttachment={loadChatAttachment} />
                  {thinkingTrimmed ? (
                    <Box
                      component="details"
                      mt="xs"
                      style={{
                        borderTop: '1px solid light-dark(var(--mantine-color-gray-3), var(--mantine-color-dark-4))',
                        paddingTop: 8,
                      }}
                    >
                      <Text
                        component="summary"
                        size="xs"
                        c="dimmed"
                        style={{ cursor: 'pointer', userSelect: 'none' }}
                      >
                        Model reasoning
                      </Text>
                      <Text
                        component="pre"
                        size="xs"
                        c="dimmed"
                        mt={6}
                        style={{
                          marginBottom: 0,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          fontFamily: 'var(--mantine-font-family-monospace)',
                          maxHeight: 240,
                          overflow: 'auto',
                        }}
                      >
                        {thinkingTrimmed}
                      </Text>
                    </Box>
                  ) : null}
                  {showMessageContinue ? (
                    <Group justify="space-between" mt="sm" wrap="nowrap" gap="sm">
                      <Text size="sm" c="dimmed">
                        The assistant has more to say.
                      </Text>
                      <Button size="xs" variant="light" loading={continuing} onClick={() => void onContinue()}>
                        Continue
                      </Button>
                    </Group>
                  ) : null}
                </>
              )
              return (
                <React.Fragment key={m.id}>
                  {showSwitchDivider ? (
                    <Divider my="xs" labelPosition="center" label={`Switched to ${thisAssistantName}`} />
                  ) : null}
                  {isUser ? (
                    <Paper withBorder p="sm" radius="md">
                      {messageBody}
                    </Paper>
                  ) : (
                    <Box p="sm">{messageBody}</Box>
                  )}
                </React.Fragment>
              )
            })
          )}
          {continuing && (
            <Paper withBorder p="sm" radius="md">
              <Text size="xs" c="dimmed" tt="uppercase" fw={700}>
                {selectedAssistant?.name || 'Assistant'}
              </Text>
              <Group gap="xs" mt={4}>
                <Loader size="xs" />
                <Text size="sm" c="dimmed">Thinking…</Text>
              </Group>
            </Paper>
          )}
          {sending && !continuing && (
            <Group gap="xs" px="sm" aria-live="polite">
              <Loader size="xs" />
              <Text size="sm" c="dimmed">
                {(selectedAssistant?.name || 'Assistant')} is thinking…
              </Text>
            </Group>
          )}
          <div ref={messagesEndRef} style={{ height: 0 }} aria-hidden />
        </Stack>
      </Box>

      <ChatComposer
        disabled={!selectedSessionId || sending || continuing}
        sending={sending}
        busy={sending || continuing}
        onSend={(text, attachments) => void onSend(text, attachments)}
        onStop={onStopGeneration}
        isMobileChatStack={isMobileChatStack}
        assistantName={selectedAssistant?.name}
      />
    </>
  )

  const chatMainColumn = (
    <Flex
      direction="column"
      className="owlbert-web-chat-main"
      style={isMobileChatStack ? { flex: 1, minHeight: 0, minWidth: 0, width: '100%' } : undefined}
    >
      {isMobileChatStack && sessionIdFromUrl ? mobileChatHeader : desktopChatHeader}
      {chatMessagesAndComposer}
    </Flex>
  )

  const showMobileList = isMobileChatStack && !sessionIdFromUrl
  const showMobileChat = isMobileChatStack && Boolean(sessionIdFromUrl)

  return (
    <>
      {showMobileList ? (
        <Flex direction="column" className="owlbert-web-chat-root" style={{ flex: 1, minHeight: 0, height: '100%' }}>
          {sessionListSidebar}
        </Flex>
      ) : showMobileChat ? (
        <Flex direction="column" className="owlbert-web-chat-root" style={{ flex: 1, minHeight: 0, height: '100%' }}>
          {chatMainColumn}
        </Flex>
      ) : (
        <Flex direction="row" className="owlbert-web-chat-root">
          {sessionListSidebar}
          {chatMainColumn}
        </Flex>
      )}
      {assistantEditorModal}
      <Modal opened={Boolean(renameTarget)} onClose={() => setRenameTarget(null)} title="Rename chat" centered>
        <Stack gap="sm">
          <TextInput
            label="Name"
            value={renameValue}
            onChange={(e) => setRenameValue(e.currentTarget.value)}
            data-autofocus
            onKeyDown={(e) => {
              if (e.key === 'Enter' && renameValue.trim() && renameTarget) {
                const id = renameTarget.sessionId
                setRenameTarget(null)
                void onRenameSession(id, renameValue)
              }
            }}
          />
          <Group justify="flex-end" gap="xs">
            <Button variant="default" onClick={() => setRenameTarget(null)}>
              Cancel
            </Button>
            <Button
              disabled={!renameValue.trim()}
              onClick={() => {
                if (!renameTarget) return
                const id = renameTarget.sessionId
                setRenameTarget(null)
                void onRenameSession(id, renameValue)
              }}
            >
              Save
            </Button>
          </Group>
        </Stack>
      </Modal>
      <Modal opened={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} title="Delete chat" centered>
        <Stack gap="md">
          <Text size="sm">
            Delete “{deleteTarget?.displayName || 'Untitled'}”? This removes the conversation and its messages. This
            cannot be undone.
          </Text>
          <Group justify="flex-end" gap="xs">
            <Button variant="default" onClick={() => setDeleteTarget(null)}>
              Cancel
            </Button>
            <Button
              color="red"
              onClick={() => {
                if (!deleteTarget) return
                const id = deleteTarget.sessionId
                setDeleteTarget(null)
                void onDeleteSession(id)
              }}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
      <AssistantCatalogPickerModal
        opened={catalogPickerOpen}
        onClose={() => setCatalogPickerOpen(false)}
        trpcClient={auth.bundle?.client ?? null}
        apiBase={auth.backendUrl}
        onPick={(p) => {
          setAssistPendingProposal(p)
          notifications.show({ color: 'teal', message: 'Template applied — review the preview, then create.' })
        }}
      />
    </>
  )
}
